import type { CampaignWelcomeContent } from "@v2/features/agent-beta/campaignContent";
import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";
import type { SignupAgentSlide, SignupShopifySlide } from "./slides";

import * as React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { CAMPAIGN_WELCOME_CONTENT } from "@v2/features/agent-beta/campaignContent";
import { AnimatePresence, motion } from "motion/react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useInterval } from "replo-utils/react/use-interval";

import { AgentCommerceStory } from "./AgentCommerceStory";
import {
  ContextGraphic,
  CustomDomainsGraphic,
  InsightsGraphic,
  IntegrationsGraphic,
  MiniappsGraphic,
  SelectAndChatGraphic,
  ShopifyContentGraphic,
  ShopifyThemePublishGraphic,
  TemplateGalleryGraphic,
  ThemeEditorGraphic,
} from "./graphics";
import { signupAgentSlides, signupShopifySlides } from "./slides";

const signupAgentSlideKeys = signupAgentSlides.map((slide) => slide.graphic);

// NOTE (Yuxin, 2026-07-25): Slide graphics are hand-built on a fixed pixel
// canvas (absolute-positioned art, fixed widths), so in any panel narrower than
// their design width — most visibly the invite modal's half-width media pane —
// they overflow and get clipped. `ScaledSlideStage` measures its container and
// uniformly scales the fixed-size art down to fit (never up), so it stays fully
// visible everywhere while wide panels render it 1:1.
const AGENT_SLIDE_DESIGN_WIDTH = 460;
const SHOPIFY_SLIDE_DESIGN_WIDTH = 510;

function ScaledSlideStage({
  designWidth,
  children,
}: React.PropsWithChildren<{ designWidth: number }>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  React.useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setScale(Math.min(1, width / designWidth));
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [designWidth]);

  return (
    <div
      ref={containerRef}
      className="flex h-[300px] w-full items-center justify-center overflow-hidden"
    >
      <div
        className="flex h-[300px] items-center justify-center"
        style={{ width: designWidth, transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
}

function HeroPathSwitchLink({
  prefix,
  linkText,
  onClick,
}: {
  prefix: string;
  linkText: string;
  onClick: (() => void) | null;
}) {
  if (!onClick) {
    return null;
  }

  return (
    <p className="text-sm leading-relaxed text-brand-seashell/65">
      {prefix}{" "}
      <button
        type="button"
        onClick={onClick}
        className="cursor-pointer text-brand-seashell underline-offset-4 transition-colors hover:underline"
      >
        {linkText}
      </button>
      ?
    </p>
  );
}

function SignupAgentSlideGraphic({
  slide,
  campaignContent,
}: {
  slide: SignupAgentSlide;
  campaignContent: CampaignWelcomeContent | null;
}) {
  return exhaustiveSwitch({ type: slide.graphic })({
    miniapps: () =>
      campaignContent ? (
        <img
          src={campaignContent.gifSrc}
          alt={campaignContent.gifAlt}
          className="h-full w-full rounded-xl object-contain"
        />
      ) : (
        <MiniappsGraphic />
      ),
    integrations: () => <IntegrationsGraphic />,
    context: () => <ContextGraphic />,
    domains: () => <CustomDomainsGraphic />,
    selectEdit: () => <SelectAndChatGraphic />,
    insights: () => <InsightsGraphic />,
  });
}

function SignupAgentSlideCopy({
  slide,
  campaignContent,
}: {
  slide: SignupAgentSlide;
  campaignContent: CampaignWelcomeContent | null;
}) {
  if (slide.graphic === "miniapps") {
    if (campaignContent) {
      return (
        <>
          <h2 className="font-editorial text-3xl">
            {campaignContent.headline}
          </h2>
          <p className="max-w-[420px] text-sm leading-relaxed text-brand-seashell/65">
            {campaignContent.subtitle}
          </p>
        </>
      );
    }
    return <AgentCommerceStory variant="carousel" />;
  }
  return (
    <>
      <h2 className="font-editorial text-3xl">{slide.title}</h2>
      <p className="max-w-[420px] text-sm leading-relaxed text-brand-seashell/65">
        {slide.description}
      </p>
    </>
  );
}

function SignupShopifySlideGraphic({ slide }: { slide: SignupShopifySlide }) {
  return exhaustiveSwitch({ type: slide.graphic })({
    editor: () => <ThemeEditorGraphic />,
    templates: () => <TemplateGalleryGraphic />,
    content: () => <ShopifyContentGraphic />,
    publish: () => <ShopifyThemePublishGraphic />,
  });
}

function SignupShopifyCarousel({
  onSwitchPath,
}: {
  onSwitchPath: (() => void) | null;
}) {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);

  useInterval(() => {
    setActiveSlideIndex(
      (currentIndex) => (currentIndex + 1) % signupShopifySlides.length,
    );
  }, 12000);

  const firstSlide = signupShopifySlides[0];
  if (!firstSlide) {
    return null;
  }
  const activeSlide = signupShopifySlides[activeSlideIndex] ?? firstSlide;

  return (
    <div className="hidden h-full w-full overflow-hidden bg-black px-16 py-12 text-brand-seashell lg:flex lg:flex-col lg:items-center lg:justify-center">
      <div className="flex w-full max-w-[560px] flex-col items-center gap-8">
        <ScaledSlideStage designWidth={SHOPIFY_SLIDE_DESIGN_WIDTH}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide.graphic}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="flex h-full w-full items-center justify-center"
            >
              <SignupShopifySlideGraphic slide={activeSlide} />
            </motion.div>
          </AnimatePresence>
        </ScaledSlideStage>
        <div className="flex min-h-[112px] flex-col items-center gap-3 text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide.title}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="flex flex-col items-center gap-3"
            >
              <h2 className="font-editorial text-3xl">{activeSlide.title}</h2>
              <p className="max-w-[420px] text-sm leading-relaxed text-brand-seashell/65">
                {activeSlide.description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-2">
          {signupShopifySlides.map((slide, index) => (
            <button
              key={slide.title}
              type="button"
              aria-label={`Show slide ${index + 1}`}
              aria-current={activeSlideIndex === index}
              onMouseEnter={() => setActiveSlideIndex(index)}
              onFocus={() => setActiveSlideIndex(index)}
              onClick={() => setActiveSlideIndex(index)}
              className={cn(
                "h-1 rounded-full transition-all duration-200",
                activeSlideIndex === index
                  ? "w-8 bg-brand-seashell"
                  : "w-4 bg-brand-seashell/30 hover:bg-brand-seashell/50",
              )}
            />
          ))}
        </div>
        <HeroPathSwitchLink
          prefix="Looking for"
          linkText="Replo Agent"
          onClick={onSwitchPath}
        />
      </div>
    </div>
  );
}

export function SignupAgentCarousel({
  className,
  campaignSource,
  onSwitchPath,
}: {
  className?: string;
  campaignSource?: string | null;
  onSwitchPath: (() => void) | null;
}) {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  // Campaign traffic gets a hero slide matching the ad that earned the click;
  // unknown or absent sources keep the default miniapps hero.
  const campaignContent =
    (campaignSource ? CAMPAIGN_WELCOME_CONTENT[campaignSource] : null) ?? null;

  useInterval(() => {
    setActiveSlideIndex(
      (currentIndex) => (currentIndex + 1) % signupAgentSlideKeys.length,
    );
  }, 12000);

  const firstSlide = signupAgentSlides[0];
  if (!firstSlide) {
    return null;
  }
  const activeSlideKey =
    signupAgentSlideKeys[activeSlideIndex] ?? firstSlide.graphic;
  const activeSlide =
    signupAgentSlides.find((slide) => slide.graphic === activeSlideKey) ??
    firstSlide;
  return (
    <div
      className={cn(
        "hidden h-full w-full overflow-hidden bg-black px-16 py-12 text-brand-seashell lg:flex lg:flex-col lg:items-center lg:justify-center",
        className,
      )}
    >
      <div className="flex w-full max-w-[560px] flex-col items-center gap-8">
        <ScaledSlideStage designWidth={AGENT_SLIDE_DESIGN_WIDTH}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlideKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="flex h-full w-full items-center justify-center"
            >
              <SignupAgentSlideGraphic
                slide={activeSlide}
                campaignContent={campaignContent}
              />
            </motion.div>
          </AnimatePresence>
        </ScaledSlideStage>
        <div className="flex h-32 shrink-0 flex-col items-center justify-center text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlideKey}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="flex flex-col items-center gap-3"
            >
              <SignupAgentSlideCopy
                slide={activeSlide}
                campaignContent={campaignContent}
              />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-2">
          {signupAgentSlideKeys.map((slideKey, index) => (
            <button
              key={slideKey}
              type="button"
              aria-label={`Show slide ${index + 1}`}
              aria-current={activeSlideIndex === index}
              onMouseEnter={() => setActiveSlideIndex(index)}
              onFocus={() => setActiveSlideIndex(index)}
              onClick={() => setActiveSlideIndex(index)}
              className={cn(
                "h-1 rounded-full transition-all duration-200",
                activeSlideIndex === index
                  ? "w-8 bg-brand-seashell"
                  : "w-4 bg-brand-seashell/30 hover:bg-brand-seashell/50",
              )}
            />
          ))}
        </div>
        <HeroPathSwitchLink
          prefix="Looking for"
          linkText="Replo's Shopify Theme Builder"
          onClick={onSwitchPath}
        />
      </div>
    </div>
  );
}

export function ProductShowcasePanel({
  defaultProjectType,
  campaignSource,
  onSwitchPath,
}: {
  defaultProjectType: WorkspaceDefaultProjectType;
  campaignSource?: string | null;
  // Null on login, where there is no signup path to switch.
  onSwitchPath: ((next: WorkspaceDefaultProjectType) => void) | null;
}) {
  if (defaultProjectType === "agent") {
    return (
      <SignupAgentCarousel
        campaignSource={campaignSource}
        onSwitchPath={
          onSwitchPath ? () => onSwitchPath("shopify_theme_builder") : null
        }
      />
    );
  }

  return (
    <SignupShopifyCarousel
      onSwitchPath={onSwitchPath ? () => onSwitchPath("agent") : null}
    />
  );
}
